/**
 * Platform detection utilities for iOS and mobile browser specific handling
 */

/**
 * Detect if the current browser is running on iOS
 * @returns {boolean} true if iOS device
 */
export function isIOS() {
	return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
}

/**
 * Detect if the current browser is Safari on iOS
 * @returns {boolean} true if iOS Safari
 */
export function isIOSSafari() {
	const ua = navigator.userAgent
	return isIOS() && /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua)
}

/**
 * Detect if the current browser is Chrome on iOS
 * @returns {boolean} true if iOS Chrome
 */
export function isIOSChrome() {
	const ua = navigator.userAgent
	return isIOS() && (/Chrome|CriOS/.test(ua))
}

/**
 * Detect if the current browser is on a mobile device
 * @returns {boolean} true if mobile device
 */
export function isMobile() {
	return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

/**
 * Detect if the current browser has iOS WebRTC quirks
 * iOS Chrome and Safari have different MediaStream handling
 * @returns {boolean} true if iOS browser with WebRTC quirks
 */
export function hasIOSWebRTCQuirks() {
	return isIOS()
}

/**
 * Detect if the browser requires special video track handling
 * iOS Chrome has specific issues with video track stop/start
 * @returns {boolean} true if requires special handling
 */
export function requiresIOSVideoWorkaround() {
	return isIOSChrome()
}

/**
 * Get platform-specific getUserMedia constraints
 * @param {boolean} enableVideo - whether video should be enabled
 * @param {boolean} enableAudio - whether audio should be enabled
 * @returns {object} platform-specific constraints
 */
export function getPlatformConstraints(enableVideo = true, enableAudio = true) {
	const baseConstraints = {
		audio: enableAudio,
		video: enableVideo
	}

	if (isIOS()) {
		// iOS-specific constraints for better compatibility
		if (enableVideo) {
			baseConstraints.video = {
				width: { ideal: 640 },
				height: { ideal: 480 },
				frameRate: { ideal: 30, max: 30 },
				facingMode: 'user'
			}
		}

		if (enableAudio) {
			baseConstraints.audio = {
				echoCancellation: true,
				noiseSuppression: true,
				autoGainControl: true
			}
		}
	}

	return baseConstraints
}

/**
 * Get platform info for debugging
 * @returns {object} platform information
 */
export function getPlatformInfo() {
	return {
		userAgent: navigator.userAgent,
		isIOS: isIOS(),
		isIOSSafari: isIOSSafari(),
		isIOSChrome: isIOSChrome(),
		isMobile: isMobile(),
		hasIOSWebRTCQuirks: hasIOSWebRTCQuirks(),
		requiresIOSVideoWorkaround: requiresIOSVideoWorkaround()
	}
}
